import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import { format } from "date-fns";

import { joinFreePass } from "../services/api/userProfile";
import { getFreePassPlansIds, getGenderFreePassPlanId } from "../utils/helperFunctions";

const FREE_PASS_PLAN_IDS = getFreePassPlansIds();

const FreePassAlert = () => {
  const { t, i18n } = useTranslation();
  const { user, apiClient } = useOutletContext();
  const queryClient = useQueryClient();

  const { mutate: mutateJoinFreePass, isPending: loading } = useMutation({
    mutationFn: joinFreePass,
    onSuccess: () => {
      queryClient.invalidateQueries(["contractPayments"]);
      toast.success(t("settingsModule.profileUpdated"));
    },
    onError: (error) => {
      console.error("Error joining Free Pass:", error);
      toast.error(t("somethingWentWrong"));
    },
  });

  const contracts = user?.contracts || [];
  const endedContracts = user?.endedContracts || [];

  const hasUsedFreePass =
    contracts.some((contract) => FREE_PASS_PLAN_IDS.includes(contract.planId)) ||
    endedContracts.some((contract) => FREE_PASS_PLAN_IDS.includes(contract.paymentPlanId));

  const date = format(new Date(), "yyyy-MM-dd");
  const onClickJoinFreePass = () =>
    mutateJoinFreePass({
      apiClient,
      data: {
        memberId: user.id,
        clubId: user.club?.id,
        contractData: {
          paymentPlanId: getGenderFreePassPlanId(user.gender), //Free Day Pass ID
          startDate: date,
          signUpDate: date,
        },
      },
    });

  if (
    !user ||
    user.hasActiveContract ||
    user.hasFrozenContracts ||
    hasUsedFreePass ||
    user.endedContractsPending ||
    (!user.endedContractsPending && user.hasEndedContracts)
  )
    return null;

  return (
    <Alert
      severity="info"
      action={
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onClickJoinFreePass}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("dashboardModule.claimFreePass")
          )}
        </Button>
      }
      sx={{
        backgroundColor: "customColors.bg2",
        marginBottom: 2,
        display: "flex",
        alignItems: "center",
        "& .MuiAlert-action": {
          marginLeft: i18n.language === "en" ? "auto" : "-8px",
          marginRight: i18n.language === "en" ? "-8px" : "auto",
        },
      }}
    >
      <Typography sx={{ marginRight: 1 }} variant="body1" color="primary">
        {t("dashboardModule.joinFreePass")}
      </Typography>
    </Alert>
  );
};

export default FreePassAlert;
